import dayjs from './dayjs'

/**
 * Local definition (avoids a circular dependency on the database package).
 * Values must match the OrganizationTimezone enum in packages/database/prisma/schema/subscription.prisma
 */
export type Timezone =
  | 'HAWAII_TIME'
  | 'ALASKA_TIME'
  | 'PACIFIC_TIME'
  | 'MOUNTAIN_TIME'
  | 'CENTRAL_TIME'
  | 'EASTERN_TIME'
  | 'ATLANTIC_TIME'
  | 'NEWFOUNDLAND_TIME'
  | 'BRAZIL_TIME'
  | 'AZORES_TIME'
  | 'COORDINATED_UNIVERSAL_TIME'
  | 'CENTRAL_EUROPEAN_TIME'
  | 'EASTERN_EUROPEAN_TIME'
  | 'MOSCOW_TIME'
  | 'GULF_TIME'
  | 'INDIA_STANDARD_TIME'
  | 'BANGLADESH_TIME'
  | 'INDOCHINA_TIME'
  | 'CHINA_STANDARD_TIME'
  | 'JAPAN_STANDARD_TIME'
  | 'AUSTRALIA_EASTERN_TIME'
  | 'NEW_ZEALAND_TIME'

export function timezoneToIANA(orgTimezone: Timezone | null): string | null {
  if (!orgTimezone) {
    return null
  }

  const timezoneMap: Record<Timezone, string> = {
    HAWAII_TIME: 'Pacific/Honolulu',
    ALASKA_TIME: 'America/Anchorage',
    PACIFIC_TIME: 'America/Los_Angeles',
    MOUNTAIN_TIME: 'America/Denver',
    CENTRAL_TIME: 'America/Chicago',
    EASTERN_TIME: 'America/New_York',
    ATLANTIC_TIME: 'America/Halifax',
    NEWFOUNDLAND_TIME: 'America/St_Johns',
    BRAZIL_TIME: 'America/Sao_Paulo',
    AZORES_TIME: 'Atlantic/Azores',
    COORDINATED_UNIVERSAL_TIME: 'UTC',
    CENTRAL_EUROPEAN_TIME: 'Europe/Berlin',
    EASTERN_EUROPEAN_TIME: 'Europe/Athens',
    MOSCOW_TIME: 'Europe/Moscow',
    GULF_TIME: 'Asia/Dubai',
    INDIA_STANDARD_TIME: 'Asia/Kolkata',
    BANGLADESH_TIME: 'Asia/Dhaka',
    INDOCHINA_TIME: 'Asia/Bangkok',
    CHINA_STANDARD_TIME: 'Asia/Shanghai',
    JAPAN_STANDARD_TIME: 'Asia/Tokyo',
    AUSTRALIA_EASTERN_TIME: 'Australia/Sydney',
    NEW_ZEALAND_TIME: 'Pacific/Auckland',
  }

  return timezoneMap[orgTimezone]
}

// Must stay in sync with the literal in the reanchor_personal_subscriptions_hawaiian_time
// migration, which can't import this. The `??` only satisfies the nullable return type.
export const HAWAII_TIME_IANA =
  timezoneToIANA('HAWAII_TIME') ?? 'Pacific/Honolulu'

// Reads `date`'s calendar day in UTC (not Hawaiian time): callers' inputs already share
// their intended day with their UTC day, and reading in HT would pull a legacy 00:00:01 UTC
// row back to the previous day.
export function anchorToHawaiianInstant(date: Date, time: string): Date {
  const calendarDate = dayjs.utc(date).format('YYYY-MM-DD')
  return dayjs.tz(`${calendarDate} ${time}`, HAWAII_TIME_IANA).utc().toDate()
}

// Suppresses the personal-subscription welcome email for back-dated start dates. A start
// date of *today* (HT) is NOT past, so it still sends. `date`'s day is read in UTC per
// anchorToHawaiianInstant's convention; "today" is read in HT, where the activation cutoff lives.
export function isBeforeTodayHawaii(date: Date): boolean {
  const calendarDate = dayjs.utc(date).format('YYYY-MM-DD')
  const todayHawaii = dayjs().tz(HAWAII_TIME_IANA).format('YYYY-MM-DD')
  return calendarDate < todayHawaii
}

// Disables the "send on subscription start date" option: it only makes sense for a future
// start date. Day read in UTC, "today" in HT, per isBeforeTodayHawaii above.
export function isTodayOrEarlierHawaii(date: Date): boolean {
  const calendarDate = dayjs.utc(date).format('YYYY-MM-DD')
  const todayHawaii = dayjs().tz(HAWAII_TIME_IANA).format('YYYY-MM-DD')
  return calendarDate <= todayHawaii
}

// Resend refuses to schedule an email more than 30 days out (exactly 30 is still allowed).
export function isMoreThan30DaysAheadHawaii(date: Date): boolean {
  const calendarDate = dayjs.utc(date).format('YYYY-MM-DD')
  const maxHawaii = dayjs()
    .tz(HAWAII_TIME_IANA)
    .add(30, 'day')
    .format('YYYY-MM-DD')
  return calendarDate > maxHawaii
}

export function getCurrentHourInTimezone(ianaTimezone: string | null): number {
  if (!ianaTimezone) {
    return new Date().getHours()
  }
  try {
    const now = dayjs()
    const tzNow = now.tz(ianaTimezone)
    return tzNow.hour()
  } catch {
    return new Date().getHours()
  }
}

/**
 * Converts a stored UTC subscription date to a DatePicker-safe Date value.
 *
 * For ORGANIZATION subscriptions: pass the org timezone so the calendar date is
 * extracted in that timezone (e.g., 2026-07-01T03:59:59Z → June 30 in EDT).
 *
 * For TRIAL subscriptions: omit timezone so the calendar date is extracted in UTC
 * (e.g., 2026-06-30T23:59:59Z → June 30 in UTC).
 *
 * The result is placed at 10:00 UTC — a safe hour that displays as the same
 * calendar day in every timezone from UTC-10 through UTC+13.
 */
export function toDatePickerValue(date: Date, tz?: Timezone | null): Date {
  const ianaTimezone = tz ? timezoneToIANA(tz) : null
  const calendarDate = ianaTimezone
    ? dayjs.utc(date).tz(ianaTimezone).format('YYYY-MM-DD')
    : dayjs.utc(date).format('YYYY-MM-DD')
  const [year, month, day] = calendarDate.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, day, 10, 0, 0, 0))
}

export function formatDateTz(
  date: Date,
  format = 'M/D/YYYY',
  tz?: Timezone
): string {
  if (tz) {
    const ianaTimezone = timezoneToIANA(tz)
    if (ianaTimezone) {
      return dayjs.utc(date).tz(ianaTimezone).format(format)
    }
  }

  return dayjs.utc(date).format(format)
}

export const todayUtcDate = (now: Date = new Date()): Date =>
  new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
